import {
  ClientDto,
  TeamDto,
  UserAdministrationDto,
} from '@act/shared/data-transfer-objects';
import { ColumnConfiguration } from '@act/storybook';
import {
  getClientNamesForUser,
  getTeamNamesForUser,
} from './UserAdminTable.utils';
import React from 'react';
import { Typography, Checkbox } from '@mui/material';
import { TeamRole } from '@act/shared/types';
import { Tooltip } from '@mui/material';

interface UserAdminTableColumnsProps {
  allTeams: TeamDto[];
  allClients: ClientDto[];
  selectedUsers?: Set<string>;
  onSelectUser?: (userId: string, selected: boolean) => void;
}

export const UserAdminTableColumns = ({
  allTeams,
  allClients,
  selectedUsers = new Set(),
  onSelectUser,
}: UserAdminTableColumnsProps): ColumnConfiguration<UserAdministrationDto>[] => [
  // Selection column
  ...(onSelectUser
    ? [
        {
          id: 'id' as keyof UserAdministrationDto,
          value: (props: UserAdministrationDto): React.ReactNode => (
            <Checkbox
              checked={selectedUsers.has(props.id)}
              onChange={e => onSelectUser(props.id, e.target.checked)}
              onClick={e => e.stopPropagation()}
              size="small"
            />
          ),
          isNumeric: false,
          shouldDisablePadding: false,
          sortable: false,
          title: 'Select' as any,
        },
      ]
    : []),
  {
    id: 'firstName' as keyof UserAdministrationDto,
    value: (props: UserAdministrationDto) =>
      `${props.firstName} ${props.lastName}`,
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Name',
  },
  {
    id: 'email' as keyof UserAdministrationDto,
    value: 'email',
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Email',
  },
  {
    id: 'role' as keyof UserAdministrationDto,
    value: (props: UserAdministrationDto) => {
      let role = props.role.replace(/_/g, ' ').toLowerCase();
      role = role.charAt(0).toUpperCase() + role.slice(1);
      role = role.replace(/\b\w/g, match => match.toUpperCase());
      return role;
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Role',
  },
  {
    id: 'avonId' as keyof UserAdministrationDto,
    value: (props: UserAdministrationDto) => {
      if (props.avonId) {
        return 'Yes';
      } else {
        return 'No';
      }
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Has Avon',
  },
  {
    id: 'teams' as keyof UserAdministrationDto,
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Teams',
    value: (props: UserAdministrationDto): React.ReactNode => {
      // Team column should show the users's primary team (role === 'member') or their first team in the list
      // and then it should say "and X more ⓘ" with a tooltip that shows the other teams if there are more than 2 teams
      const memberTeam = props.teams.find(
        team => team.role === TeamRole.MEMBER
      );
      const mainTeam = memberTeam || props.teams.length ? props.teams[0] : null;
      const otherTeams =
        props.teams?.length > 1
          ? props.teams.filter(team => team.teamId !== mainTeam?.teamId)
          : [];

      const mainTeamText = mainTeam
        ? getTeamNamesForUser([mainTeam], allTeams)[0]
        : 'No Teams';
      const otherTeamText = getTeamNamesForUser(otherTeams, allTeams).join(
        '\n'
      );

      return (
        <Typography variant="body2" component="div">
          {mainTeamText}
          {!!otherTeams.length && (
            <Tooltip
              title={
                <Typography style={{ whiteSpace: 'pre-line' }} variant="body2">
                  {otherTeamText}
                </Typography>
              }
            >
              <span>and {otherTeams.length} more ⓘ</span>
            </Tooltip>
          )}
        </Typography>
      );
    },
  },
  {
    id: 'managedClients' as keyof UserAdministrationDto,
    value: (props: UserAdministrationDto) => {
      const clientNames = getClientNamesForUser(
        props.managedClients,
        allClients
      );
      const clientNameRefined = clientNames.join('\n');
      return (
        <Typography
          style={{ whiteSpace: 'pre-line' }}
          variant="body2"
          component="div"
        >
          {clientNameRefined}
        </Typography>
      );
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Managed Clients',
  },
  {
    id: 'hasAppAccess' as keyof UserAdministrationDto,
    value: (props: UserAdministrationDto) => {
      return props.hasAppAccess === true ? 'Yes' : 'No';
    },
    isNumeric: false,
    shouldDisablePadding: false,
    sortable: true,
    title: 'Has App Access',
  },
];
